import AgencyOnboardingModalShell, { AGENCY_ONBOARDING_MODAL_NAME } from '@root/joinroot.com/src/components/agents/agency-onboarding-modal/agency-onboarding-modal-shell';
import Button, { BUTTON_VARIANT_SECONDARY } from '@root/core/src/components/button';
import Input, { TextTypes } from '@root/core/src/components/input';
import PropTypes from '@root/vendor/prop-types';
import React, { useMemo, useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import Select from '@root/core/src/components/select/select';
import shuffle from '@root/vendor/lodash/shuffle';
import useForm from '@root/core/src/hooks/use-form';
import useSafeImperativeNetworkRequest from '@root/core/src/hooks/use-safe-imperative-network-request';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { FORM_NAME_OPTIONS, submitAttributionFormConfiguration } from '@root/attribution/src/api/submit-attribution-form/submit-attribution-form-configuration';
import {
  isFormatted,
  isRequired,
  isValidEmail,
  isValidPhoneNumber,
} from '@root/core/src/utils/validators';
import { useRootId } from '@root/attribution/src/hooks/use-root-id';

export const LINKEDIN_INDEPENDENT_AGENTS_MARKETING_CONVERSION_ID = 24665852;

/** Pipedrive ID-related values have been obtained from our hosted webform's definition from the Pipedrive web API
     Webform 'https://webforms.pipedrive.com/f/6rG12Tu4n12SXg5Vu3hO070BVWPwm6BrctmOz6jCG0Vxj2mGxAfozpTiCz7BjAT65Z?_cl=2VbZTP0AF7PPxqeok19ZXMWZ'
     - ID may be utilized to request the definition at 'https://webforms.pipedrive.com/f/{formId}/definition' to obtain field keys and other configuration details necessary for form submission, like:
       `curl 'https://webforms.pipedrive.com/f/6rG12Tu4n12SXg5Vu3hO070BVWPwm6BrctmOz6jCG0Vxj2mGxAfozpTiCz7BjAT65Z/definition'`
*/

export const AGENCY_TYPE = {
  INDEPENDENT_AGENCY: 42,
  INDEPENDENT_SOLE_PROPRIETOR: 43,
  AGGREGATOR: 44,
  MARKET_ACCESS_PROVIDER: 45,
  WHOLESALER: 46,
};

const AGENCY_TYPE_OPTIONS = [
  {
    label: 'Independent Agency',
    value: AGENCY_TYPE.INDEPENDENT_AGENCY,
  },
  {
    label: 'Independent Sole Proprietor',
    value: AGENCY_TYPE.INDEPENDENT_SOLE_PROPRIETOR,
  },
  {
    label: 'Aggregator',
    value: AGENCY_TYPE.AGGREGATOR,
  },
  {
    label: 'Market Access Provider',
    value: AGENCY_TYPE.MARKET_ACCESS_PROVIDER,
  },
  {
    label: 'Wholesaler',
    value: AGENCY_TYPE.WHOLESALER,
  },
];

export const COMP_RATER = {
  EZLYNX: 33,
  PL_RATING: 34,
  QUINSTREET: 35,
  INTERNAL_RATER: 36,
  OTHER: 37,
  NONE: 47,
};

const COMPARATIVE_RATER_OPTIONS = [
  {
    label: 'EZLynx',
    value: COMP_RATER.EZLYNX,
  },
  {
    label: 'PL Rating',
    value: COMP_RATER.PL_RATING,
  },
  {
    label: 'QuinStreet',
    value: COMP_RATER.QUINSTREET,
  },
  {
    label: 'Agency\'s internal rater',
    value: COMP_RATER.INTERNAL_RATER,
  },
  {
    label: 'Other',
    value: COMP_RATER.OTHER,
  },
  {
    label: 'My agency does not use a comparative rater',
    value: COMP_RATER.NONE,
  },
];

export const LEAD_SOURCE = {
  ONLINE_ADVERTISEMENT: 91,
  EZLYNX: 92,
  TRUSTED_CHOICE: 93,
  TURBORATER: 94,
  PL_RATING: 95,
  EMAIL_FROM_ROOT: 96,
  PHONE_CALL_FROM_ROOT: 97,
  PHYSICAL_MAIL_ADVERTISEMENT: 107,
  FRIEND_COLLEAGUE: 98,
  FACEBOOK_META: 114,
  AMERICAN_AGENTS_ALLIANCE_CONFERENCE_EXPO: 111,
  GOOGLE_SEARCH: 115,
  PRINTED_MEDIA: 116,
  OTHER: 99,
};

const HEARD_ABOUT_ROOT_OPTIONS = [
  {
    label: 'Online Advertisement',
    value: LEAD_SOURCE.ONLINE_ADVERTISEMENT,
  },
  {
    label: 'EZLynx',
    value: LEAD_SOURCE.EZLYNX,
  },
  {
    label: 'TrustedChoice',
    value: LEAD_SOURCE.TRUSTED_CHOICE,
  },
  {
    label: 'TurboRater',
    value: LEAD_SOURCE.TURBORATER,
  },
  {
    label: 'PL Rating',
    value: LEAD_SOURCE.PL_RATING,
  },
  {
    label: 'Email from Root',
    value: LEAD_SOURCE.EMAIL_FROM_ROOT,
  },
  {
    label: 'Phone Call from Root',
    value: LEAD_SOURCE.PHONE_CALL_FROM_ROOT,
  },
  {
    label: 'Physical Mail Advertisement',
    value: LEAD_SOURCE.PHYSICAL_MAIL_ADVERTISEMENT,
  },
  {
    label: 'Friend/Colleague',
    value: LEAD_SOURCE.FRIEND_COLLEAGUE,
  },
  {
    label: 'Facebook/Meta',
    value: LEAD_SOURCE.FACEBOOK_META,
  },
  {
    label: 'American Agents Alliance Conference & Expo',
    value: LEAD_SOURCE.AMERICAN_AGENTS_ALLIANCE_CONFERENCE_EXPO,
  },
  {
    label: 'Google Search',
    value: LEAD_SOURCE.GOOGLE_SEARCH,
  },
  {
    label: 'Printed Media',
    value: LEAD_SOURCE.PRINTED_MEDIA,
  },
  {
    label: 'Other',
    value: LEAD_SOURCE.OTHER,
  },
];

const REQUIRED_FIELD_ERROR = 'Required field';
const NUMBERS_ONLY_ERROR = 'Please enter numbers only';
const WHOLE_NUMBER_ERROR = 'Please enter a whole number';
const VALID_EMAIL_ERROR = 'Please enter a valid email address';
const VALID_PHONE_ERROR = 'Please enter a valid phone number';
const VALID_WEBSITE_ERROR = 'Please enter a valid website';
const VALID_AMOUNT_ERROR = 'Please enter a valid amount';
const VALID_PERCENTAGE_ERROR = 'Enter a value between 0 and 100';
const VALID_IVANS_Y_CODE_ERROR = 'Please enter a valid Ivans Y-Code (5 letters beginning with Y)';
const VALID_IVANS_MAILBOX_ID_ERROR = 'Please enter a valid Ivans Mailbox ID (7 characters beginning with Y followed by 6 letters or numbers)';

const DIGITS_ONLY_REGEX = /^\d+$/;
const IVANS_Y_CODE_REGEX = /^[Yy][a-zA-Z]{4}$/;
const IVANS_MAILBOX_ID_REGEX = /^[Yy][a-zA-Z0-9]{6}$/;

const trimWhitespace = (value = '') => value.trim();
const trimLeadingWhitespace = (value = '') => value.replace(/^\s+/, '');
const digitsOnly = (value = '') => value.toString().replace(/\D/g, '');

const isValidWebsiteField = (fieldName) => (values, errors) => {
  const value = values[fieldName];
  if (!value) {
    return;
  }

  const website = value.trim();

  if (!website) {
    errors[fieldName] = VALID_WEBSITE_ERROR;
    return;
  }

  try {
    const withProtocol = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    const parsed = new URL(withProtocol);
    if (!parsed.hostname || !parsed.hostname.includes('.')) {
      errors[fieldName] = VALID_WEBSITE_ERROR;
    }
  } catch {
    errors[fieldName] = VALID_WEBSITE_ERROR;
  }
};

const hasAtLeastOneDigit = (fieldName, errorMessage) => (values, errors) => {
  const value = values[fieldName];
  if (value && !/\d/.test(value)) {
    errors[fieldName] = errorMessage;
  }
};

const isPercentageBetweenZeroAndHundred = (fieldName) => (values, errors) => {
  const value = values[fieldName];
  if (!value) {
    return;
  }

  const percentage = Number(value.toString().replace('%', '').trim());
  if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
    errors[fieldName] = VALID_PERCENTAGE_ERROR;
  }
};

const isValidIvansYCode = (fieldName) => (values, errors) => {
  const value = values[fieldName];
  if (value && !IVANS_Y_CODE_REGEX.test(value)) {
    errors[fieldName] = VALID_IVANS_Y_CODE_ERROR;
  }
};

const isValidIvansMailboxId = (fieldName) => (values, errors) => {
  const value = values[fieldName];
  if (value && !IVANS_MAILBOX_ID_REGEX.test(value)) {
    errors[fieldName] = VALID_IVANS_MAILBOX_ID_ERROR;
  }
};

const STEP_KEYS = {
  AGENT: 'agent',
  AGENCY: 'agency',
  BUSINESS: 'business',
};
const STEP_ORDER = [STEP_KEYS.AGENT, STEP_KEYS.AGENCY, STEP_KEYS.BUSINESS];

export const FORM_FIELD_KEYS = {
  LEGAL_NAME: 'legalName',
  PRINCIPAL_AGENT_NPN: 'principalAgentNpn',
  EMAIL_ADDRESS: 'emailAddress',
  PHONE_NUMBER: 'phoneNumber',
  AGENCY_NAME: 'agencyName',
  AGENCY_TYPE: 'agencyType',
  AGENCY_WEBSITE: 'agencyWebsite',
  AGENCY_NPN: 'agencyNpn',
  AGENCY_ADDRESS: 'agencyAddress',
  ADDITIONAL_PRODUCERS: 'additionalProducers',
  IVANS_Y_CODE: 'ivansYCode',
  IVANS_MAILBOX_ID: 'ivansMailboxId',
  TOTAL_WRITTEN_PREMIUM: 'totalWrittenPremium',
  PERSONAL_LINES_AUTO_PERCENTAGE: 'personalLinesAutoPercentage',
  YEARS_IN_BUSINESS: 'yearsInBusiness',
  COMPARATIVE_RATER: 'comparativeRater',
  HEARD_ABOUT_ROOT: 'heardAboutRoot',
  TOP_CARRIERS: 'topCarriers',
};
const OPTIONAL_FORM_FIELD_KEYS = [
  FORM_FIELD_KEYS.IVANS_Y_CODE,
  FORM_FIELD_KEYS.IVANS_MAILBOX_ID,
];

const STEP_LABELS = {
  [STEP_KEYS.AGENT]: 'Agent',
  [STEP_KEYS.AGENCY]: 'Agency',
  [STEP_KEYS.BUSINESS]: 'Business',
};

export const FORM_FIELD_LABELS = {
  [FORM_FIELD_KEYS.LEGAL_NAME]: 'Legal name (first & last)',
  [FORM_FIELD_KEYS.PRINCIPAL_AGENT_NPN]: 'National Producer Number',
  [FORM_FIELD_KEYS.EMAIL_ADDRESS]: 'Email address',
  [FORM_FIELD_KEYS.PHONE_NUMBER]: 'Phone number',
  [FORM_FIELD_KEYS.AGENCY_NAME]: 'Agency name',
  [FORM_FIELD_KEYS.AGENCY_TYPE]: 'Agency type',
  [FORM_FIELD_KEYS.AGENCY_WEBSITE]: 'Agency website',
  [FORM_FIELD_KEYS.AGENCY_NPN]: 'Agency National Producer Number',
  [FORM_FIELD_KEYS.AGENCY_ADDRESS]: 'Agency address',
  [FORM_FIELD_KEYS.ADDITIONAL_PRODUCERS]: 'How many additional producers would you like to be appointed with Root?',
  [FORM_FIELD_KEYS.IVANS_Y_CODE]: 'Ivans Y-Code (optional)',
  [FORM_FIELD_KEYS.IVANS_MAILBOX_ID]: 'Ivans Mailbox ID (optional)',
  [FORM_FIELD_KEYS.TOTAL_WRITTEN_PREMIUM]: 'What is your agency\'s total written premium?',
  [FORM_FIELD_KEYS.PERSONAL_LINES_AUTO_PERCENTAGE]: 'What percentage is personal lines auto?',
  [FORM_FIELD_KEYS.YEARS_IN_BUSINESS]: 'How many years has your agency been in business?',
  [FORM_FIELD_KEYS.COMPARATIVE_RATER]: 'Does your agency use a comparative rater? If so, which one?',
  [FORM_FIELD_KEYS.HEARD_ABOUT_ROOT]: 'How did you hear about Root?',
  [FORM_FIELD_KEYS.TOP_CARRIERS]: 'Please provide the top three carriers your agency writes the most auto insurance with',
};

const STEP_HEADINGS = {
  [STEP_KEYS.AGENT]: 'First, let\'s start with your Principal Agent information.',
  [STEP_KEYS.AGENCY]: 'Next, tell us about your agency.',
  [STEP_KEYS.BUSINESS]: 'A few more details and we\'ll get your application on its way.',
};

const STEP_FIELDS = {
  [STEP_KEYS.AGENT]: [
    FORM_FIELD_KEYS.LEGAL_NAME,
    FORM_FIELD_KEYS.PRINCIPAL_AGENT_NPN,
    FORM_FIELD_KEYS.EMAIL_ADDRESS,
    FORM_FIELD_KEYS.PHONE_NUMBER,
  ],
  [STEP_KEYS.AGENCY]: [
    FORM_FIELD_KEYS.AGENCY_NAME,
    FORM_FIELD_KEYS.AGENCY_TYPE,
    FORM_FIELD_KEYS.AGENCY_WEBSITE,
    FORM_FIELD_KEYS.AGENCY_NPN,
    FORM_FIELD_KEYS.AGENCY_ADDRESS,
    FORM_FIELD_KEYS.ADDITIONAL_PRODUCERS,
    FORM_FIELD_KEYS.IVANS_Y_CODE,
    FORM_FIELD_KEYS.IVANS_MAILBOX_ID,
  ],
  [STEP_KEYS.BUSINESS]: [
    FORM_FIELD_KEYS.TOTAL_WRITTEN_PREMIUM,
    FORM_FIELD_KEYS.PERSONAL_LINES_AUTO_PERCENTAGE,
    FORM_FIELD_KEYS.YEARS_IN_BUSINESS,
    FORM_FIELD_KEYS.COMPARATIVE_RATER,
    FORM_FIELD_KEYS.HEARD_ABOUT_ROOT,
    FORM_FIELD_KEYS.TOP_CARRIERS,
  ],
};

const REQUIRED_FIELDS = Object.values(FORM_FIELD_KEYS).filter((fieldKey) => !OPTIONAL_FORM_FIELD_KEYS.includes(fieldKey));

const validations = [
  ...REQUIRED_FIELDS.map((fieldName) => isRequired(fieldName, REQUIRED_FIELD_ERROR)),
  isValidEmail(FORM_FIELD_KEYS.EMAIL_ADDRESS, VALID_EMAIL_ERROR),
  isValidPhoneNumber(FORM_FIELD_KEYS.PHONE_NUMBER, VALID_PHONE_ERROR),
  isFormatted(FORM_FIELD_KEYS.PRINCIPAL_AGENT_NPN, DIGITS_ONLY_REGEX, NUMBERS_ONLY_ERROR),
  isValidWebsiteField(FORM_FIELD_KEYS.AGENCY_WEBSITE),
  isFormatted(FORM_FIELD_KEYS.AGENCY_NPN, DIGITS_ONLY_REGEX, NUMBERS_ONLY_ERROR),
  isFormatted(FORM_FIELD_KEYS.ADDITIONAL_PRODUCERS, DIGITS_ONLY_REGEX, WHOLE_NUMBER_ERROR),
  isValidIvansYCode(FORM_FIELD_KEYS.IVANS_Y_CODE),
  isValidIvansMailboxId(FORM_FIELD_KEYS.IVANS_MAILBOX_ID),
  hasAtLeastOneDigit(FORM_FIELD_KEYS.TOTAL_WRITTEN_PREMIUM, VALID_AMOUNT_ERROR),
  isPercentageBetweenZeroAndHundred(FORM_FIELD_KEYS.PERSONAL_LINES_AUTO_PERCENTAGE),
  isFormatted(FORM_FIELD_KEYS.YEARS_IN_BUSINESS, DIGITS_ONLY_REGEX, WHOLE_NUMBER_ERROR),
];

export default function AgencyOnboardingModal({
  anchor,
  isShowing,
  onClose,
  sectionSpacing = {},
  subtitle,
  title,
  triggerLabel,
}) {
  const [isUncontrolledShowing, setIsUncontrolledShowing] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [submission, setSubmission] = useState(null);

  const rootId = useRootId();
  const shuffledHeardAboutRootOptions = useMemo(() => shuffle(HEARD_ABOUT_ROOT_OPTIONS), []);

  const form = useForm({
    initialValues: Object.values(FORM_FIELD_KEYS).reduce((acc, key) => {
      return {
        ...acc,
        [key]: '',
      };
    }, {}),
    validations,
    validateOnBlur: true,
  });

  const isControlled = typeof isShowing === 'boolean';
  const shouldShowModal = isControlled ? isShowing : isUncontrolledShowing;
  const currentStepKey = STEP_ORDER[currentStepIndex];
  const isComplete = currentStepKey === undefined;

  const openModal = () => {
    if (!isControlled) {
      setIsUncontrolledShowing(true);
    }
  };

  const closeModal = () => {
    if (!isControlled) {
      setIsUncontrolledShowing(false);
    }

    onClose();
  };

  const [createAgencyOnboardingEntities, isCreatingAgency] = useSafeImperativeNetworkRequest(submitAttributionFormConfiguration);

  const callCreateEntities = async (formValues) => {
    await createAgencyOnboardingEntities({
      rootId,
      formName: FORM_NAME_OPTIONS.AGENCY_ONBOARDING,
      formValues,
    });
  };

  const onComplete = async (values) => {
    setSubmission({
      values,
      meta: {
        completedAt: new Date().toISOString(),
        source: AGENCY_ONBOARDING_MODAL_NAME,
        stepsCompleted: STEP_ORDER.length,
      },
    });
    await callCreateEntities(values);
    trackAdConversion();
    setCurrentStepIndex(STEP_ORDER.length);
  };

  const handleBack = () => {
    setCurrentStepIndex((previousIndex) => Math.max(previousIndex - 1, 0));
  };

  const handleContinue = async (event) => {
    event.preventDefault();

    const stepErrors = {};
    validations.forEach((validation) => validation(form.values, stepErrors));

    const firstFieldWithError = STEP_FIELDS[currentStepKey].find((fieldName) => !!stepErrors[fieldName]);
    if (firstFieldWithError) {
      form.setFieldError(firstFieldWithError, stepErrors[firstFieldWithError]);
      return;
    }

    if (currentStepIndex === STEP_ORDER.length - 1) {
      await onComplete(form.values);
      return;
    }

    setCurrentStepIndex(currentStepIndex + 1);
  };

  const trackAdConversion = () => {
    if (typeof window.lintrk !== 'undefined') {
      window.lintrk('track', { conversion_id: LINKEDIN_INDEPENDENT_AGENTS_MARKETING_CONVERSION_ID });
    }
  };

  const sectionSpacingStyles = {
    paddingTop: (sectionSpacing.top || 0) / 1.25,
    paddingBottom: (sectionSpacing.bottom || 0) / 1.25,
    ...Responsive.md({
      paddingTop: sectionSpacing.top || 0,
      paddingBottom: sectionSpacing.bottom || 0,
    }),
  };

  const renderStepIndicators = () => (
    <div css={styles.stepIndicators}>
      {STEP_ORDER.map((stepKey, index) => {
        const isActive = currentStepIndex === index;
        const isCompleted = currentStepIndex > index;

        return (
          <React.Fragment key={stepKey}>
            <div css={styles.stepItem}>
              <div css={[styles.stepCircle, isActive && styles.stepCircleActive, isCompleted && styles.stepCircleComplete]}>
                {index + 1}
              </div>
              <span css={[styles.stepLabel, isActive && styles.stepLabelActive]}>{STEP_LABELS[stepKey]}</span>
            </div>
            {index < STEP_ORDER.length - 1 && <div css={styles.stepDivider} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStepBody = () => {
    if (currentStepKey === STEP_KEYS.AGENT) {
      return (
        <>
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.LEGAL_NAME]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.LEGAL_NAME, { sanitize: trimLeadingWhitespace })}
          />
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.PRINCIPAL_AGENT_NPN]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.PRINCIPAL_AGENT_NPN, { sanitize: digitsOnly })}
          />
          <Input
            inputType={TextTypes.EMAIL}
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.EMAIL_ADDRESS]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.EMAIL_ADDRESS, { sanitize: trimWhitespace })}
          />
          <Input
            inputType={TextTypes.TEL}
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.PHONE_NUMBER]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.PHONE_NUMBER, { sanitize: digitsOnly })}
          />
        </>
      );
    }

    if (currentStepKey === STEP_KEYS.AGENCY) {
      const agencyTypeField = form.getFieldProps(FORM_FIELD_KEYS.AGENCY_TYPE);

      return (
        <>
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.AGENCY_NAME]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.AGENCY_NAME, { sanitize: trimLeadingWhitespace })}
          />
          <div css={styles.selectWrapper}>
            <Select
              errorLabel={agencyTypeField.errorLabel}
              inputId={FORM_FIELD_KEYS.AGENCY_TYPE}
              inputName={FORM_FIELD_KEYS.AGENCY_TYPE}
              onChange={agencyTypeField.onChange}
              options={AGENCY_TYPE_OPTIONS}
              placeholder={FORM_FIELD_LABELS[FORM_FIELD_KEYS.AGENCY_TYPE]}
              selectedValue={agencyTypeField.value}
            />
          </div>
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.AGENCY_WEBSITE]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.AGENCY_WEBSITE, { sanitize: trimWhitespace })}
          />
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.AGENCY_NPN]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.AGENCY_NPN, { sanitize: digitsOnly })}
          />
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.AGENCY_ADDRESS]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.AGENCY_ADDRESS, { sanitize: trimLeadingWhitespace })}
          />
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.ADDITIONAL_PRODUCERS]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.ADDITIONAL_PRODUCERS, { sanitize: digitsOnly })}
          />
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.IVANS_Y_CODE]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.IVANS_Y_CODE, { sanitize: trimWhitespace })}
          />
          <Input
            label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.IVANS_MAILBOX_ID]}
            wrapperStyle={styles.inputWrapper}
            {...form.getFieldProps(FORM_FIELD_KEYS.IVANS_MAILBOX_ID, { sanitize: trimWhitespace })}
          />
        </>
      );
    }

    const comparativeRaterField = form.getFieldProps(FORM_FIELD_KEYS.COMPARATIVE_RATER);
    const heardAboutRootField = form.getFieldProps(FORM_FIELD_KEYS.HEARD_ABOUT_ROOT);

    return (
      <>
        <Input
          label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.TOTAL_WRITTEN_PREMIUM]}
          wrapperStyle={styles.inputWrapper}
          {...form.getFieldProps(FORM_FIELD_KEYS.TOTAL_WRITTEN_PREMIUM, { sanitize: trimLeadingWhitespace })}
        />
        <Input
          label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.PERSONAL_LINES_AUTO_PERCENTAGE]}
          wrapperStyle={styles.inputWrapper}
          {...form.getFieldProps(FORM_FIELD_KEYS.PERSONAL_LINES_AUTO_PERCENTAGE, { sanitize: trimWhitespace })}
        />
        <Input
          label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.YEARS_IN_BUSINESS]}
          wrapperStyle={styles.inputWrapper}
          {...form.getFieldProps(FORM_FIELD_KEYS.YEARS_IN_BUSINESS, { sanitize: digitsOnly })}
        />
        <div css={styles.selectWrapper}>
          <Select
            errorLabel={comparativeRaterField.errorLabel}
            inputId={FORM_FIELD_KEYS.COMPARATIVE_RATER}
            inputName={FORM_FIELD_KEYS.COMPARATIVE_RATER}
            onChange={comparativeRaterField.onChange}
            options={COMPARATIVE_RATER_OPTIONS}
            placeholder={FORM_FIELD_LABELS[FORM_FIELD_KEYS.COMPARATIVE_RATER]}
            selectedValue={comparativeRaterField.value}
          />
        </div>
        <div css={styles.selectWrapper}>
          <Select
            errorLabel={heardAboutRootField.errorLabel}
            inputId={FORM_FIELD_KEYS.HEARD_ABOUT_ROOT}
            inputName={FORM_FIELD_KEYS.HEARD_ABOUT_ROOT}
            onChange={heardAboutRootField.onChange}
            options={shuffledHeardAboutRootOptions}
            placeholder={FORM_FIELD_LABELS[FORM_FIELD_KEYS.HEARD_ABOUT_ROOT]}
            selectedValue={heardAboutRootField.value}
          />
        </div>
        <Input
          label={FORM_FIELD_LABELS[FORM_FIELD_KEYS.TOP_CARRIERS]}
          wrapperStyle={styles.inputWrapper}
          {...form.getFieldProps(FORM_FIELD_KEYS.TOP_CARRIERS, { sanitize: trimLeadingWhitespace })}
        />
      </>
    );
  };

  const renderFooter = () => {
    if (isComplete) {
      return (
        <div css={styles.completeFooter}>
          <Button
            onClick={closeModal}
            type={'button'}
          >
            {'Close'}
          </Button>
        </div>
      );
    }

    return (
      <>
        <div>
          {currentStepIndex > 0 &&
            <Button
              onClick={handleBack}
              type={'button'}
              variant={BUTTON_VARIANT_SECONDARY}
            >
              {'Back'}
            </Button>
          }
        </div>
        <Button
          disabled={isCreatingAgency}
          onClick={handleContinue}
          type={'button'}
        >
          {'Continue'}
        </Button>
      </>
    );
  };

  const renderBody = () => {
    if (isComplete) {
      const submittedEmail = submission?.values?.emailAddress || form.values.emailAddress;

      return (
        <div css={styles.completeBody}>
          <p css={styles.rootWordmark}>{'Root'}</p>
          <h3 css={styles.completeHeading}>{'Application submitted.'}</h3>
          <p css={styles.completeCopy}>{'Thank you for your interest in partnering with Root. Our team will review your application and contact you soon.'}</p>
          <div css={styles.nextStepsCard}>
            <p css={styles.nextStepsTitle}>{'What happens next?'}</p>
            <ul css={styles.nextStepsList}>
              <li>{'Our team reviews your application'}</li>
              <li>{`You'll receive a confirmation email at ${submittedEmail || 'your email address'}`}</li>
              <li>{'A representative will call you to discuss next steps'}</li>
            </ul>
          </div>
        </div>
      );
    }

    return (
      <form
        css={styles.form}
        onSubmit={handleContinue}
      >
        {renderStepIndicators()}
        <h3 css={styles.stepHeading}>{STEP_HEADINGS[currentStepKey]}</h3>
        <div css={styles.fields}>{renderStepBody()}</div>
      </form>
    );
  };

  return (
    <section
      css={[styles.section, sectionSpacingStyles]}
      data-testid={'agency-onboarding-modal-container'}
      id={anchor}
    >
      {triggerLabel &&
        <Button
          css={styles.triggerButton}
          data-testid={'agency-onboarding-modal-trigger'}
          onClick={openModal}
          type={'button'}
        >
          {triggerLabel}
        </Button>
      }
      <AgencyOnboardingModalShell
        footer={renderFooter()}
        isShowing={shouldShowModal}
        onClose={closeModal}
        subtitle={subtitle}
        title={title}
      >
        {renderBody()}
      </AgencyOnboardingModalShell>
    </section>
  );
}

AgencyOnboardingModal.propTypes = {
  anchor: PropTypes.string,
  isShowing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  sectionSpacing: PropTypes.shape({
    bottom: PropTypes.number,
    top: PropTypes.number,
  }),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  triggerLabel: PropTypes.string,
};

const styles = StyleSheet.create({
  section: {
    minWidth: '220px',
  },
  triggerButton: {
    maxWidth: '240px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  stepIndicators: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 620,
    margin: '0 auto',
    marginBottom: 4,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '0 0 auto',
  },
  stepCircle: {
    ...Theme.paragraph2({
      bold: true,
    }),
    color: Colors.gray50(),
    width: 18,
    height: 18,
    borderRadius: 18,
    border: `1px solid ${Colors.gray40()}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepCircleActive: {
    borderColor: Colors.rootOrange(),
    color: Colors.rootOrange(),
  },
  stepCircleComplete: {
    borderColor: Colors.gray50(),
    color: Colors.gray50(),
  },
  stepLabel: {
    ...Theme.paragraph2(),
    color: Colors.gray50(),
  },
  stepLabelActive: {
    ...Theme.paragraph2({
      bold: true,
    }),
    color: Colors.nearBlack(),
  },
  stepDivider: {
    flex: '1 1 0%',
    height: 1,
    backgroundColor: Colors.gray30(),
    margin: '0 12px',
  },
  stepHeading: {
    ...Theme.heading5(),
    margin: 0,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  inputWrapper: {
    marginTop: 0,
  },
  selectWrapper: {
    '& > div': {
      width: '100%',
    },
  },
  completeBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 14,
    paddingTop: 6,
  },
  rootWordmark: {
    ...Theme.displayHeading3(),
    color: Colors.rootOrange(),
    margin: 0,
  },
  completeHeading: {
    ...Theme.heading1(),
    margin: 0,
  },
  completeCopy: {
    ...Theme.paragraph1(),
    maxWidth: 540,
    margin: 0,
  },
  nextStepsCard: {
    marginTop: 4,
    width: '100%',
    maxWidth: 420,
    border: `1px solid ${Colors.gray20()}`,
    borderRadius: 10,
    backgroundColor: Colors.gray10(),
    padding: '18px 18px 14px',
  },
  nextStepsTitle: {
    ...Theme.paragraph1({
      bold: true,
    }),
    color: Colors.nearBlack(),
    marginTop: 0,
    marginBottom: 10,
  },
  nextStepsList: {
    ...Theme.paragraph2(),
    color: Colors.nearBlack(),
    margin: 0,
    paddingLeft: 20,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  completeFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
});
